import dayjs from "dayjs";
import { useState } from "react";

const normalizeKey = (key = "") => key.replace("Reg.", "").split(" ").join("");

const isDateField = (key: string) => key.includes("ExpiryDate");

const formatDate = (date?: string) => (date ? dayjs(date).format("YYYY-MM-DD") : "");

export const useMaskedFields = (maskingFields: string[]) => {
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>({});

  // -----------------------------
  // INIT (normalize everything here)
  // -----------------------------
  const initialize = (entries: any[]) => {
    const temp: Record<string, any> = {};

    entries.forEach(({ key, value }) => {
      const norm = normalizeKey(key);
      let val = value === "N/A" ? "" : value;

      if (!maskingFields.includes(norm)) return;

      // ✅ normalize dates here
      if (isDateField(norm)) {
        val = val ? dayjs(val, "DD-MM-YYYY").format("YYYY-MM-DD") : "";
      }

      temp[norm] = val;
    });

    setOriginalValues(temp);
    setEditingFields({});
  };

  // -----------------------------
  // EDIT MODE
  // -----------------------------
  const setEditMode = (field: string, setValue: any) => {
    setValue(field, "", {
      shouldValidate: true,
      shouldDirty: false,
    });

    setEditingFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  // -----------------------------
  // RESET FIELD
  // -----------------------------
  const resetField = (field: string, setValue: any) => {
    const original = originalValues[field] ?? "";

    setValue(field, original, {
      shouldValidate: true,
      shouldDirty: false,
    });

    setEditingFields((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  // -----------------------------
  // MAIN DIFF LOGIC
  // -----------------------------
  const preparePayload = (currentValues: any) => {
    const payload: any = {};

    maskingFields.forEach((field) => {
      let current = currentValues[field];
      let original = originalValues[field];

      // normalize select
      if (current && typeof current === "object" && "value" in current) {
        current = current.value;
      }

      // normalize date (important)
      if (isDateField(field)) {
        current = formatDate(current);
        original = formatDate(original);
      }

      // skip if same
      if (current === original) return;

      const isEmpty = current === "" || current === null || current === undefined;

      // allow clearing
      if (isEmpty && original) {
        payload[field] = current;
        return;
      }

      payload[field] = current;
    });

    return payload;
  };

  return {
    originalValues,
    editingFields,
    initialize,
    setEditMode,
    resetField,
    preparePayload,
  };
};
